// -----------------------------------------------------------------------------
// Purpose:
// - Backoffice workflow page for reviewing a Tender while it is "In Quotation".
// - Loads Tender data, phases, carriers, lanes and quote-related aggregates.
// - Supports FOUR inline views:
//      1) In-Quotation overview
//      2) Carrier detail
//      3) Lane view
//      4) Summary view
// - Persists only lightweight UI state per tab so users can switch tabs without
//   losing navigation context.
// - Reloads heavy datasets from the backend when the tab is reconstructed.
// -----------------------------------------------------------------------------

import React, {
  useEffect,
  useLayoutEffect,
  useState,
  useContext,
  useMemo,
  useRef,
  useCallback,
} from "react";
import { useSelector, useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import {
  LoadingContext,
  MHeader,
  MButton,
  BufferService,
  MMessageBoard,
  M_StateActions,
  NavigateContext,
  MSelect,
} from "src/M_Components";
import PropTypes from "prop-types";
import { useNavigate } from "react-router-dom";
import { isFailurePayload, apiMessage } from "src/components/utils/apiResponse";

import InQuotationForm from "../../components/Tenders/InQuotationForm";
import CarrierResponsesTable from "../../components/Tenders/carrierResponsesTable";
import TenderQuotesAnalysis from "../../components/Tenders/TenderQuoteAnalysis";
import CarrierResponseDetailTable from "../../components/Tenders/CarrierResponseDetailTable";
import ViewLaneTable from "../../components/Tenders/ViewLaneTable";
import TenderSummaryTable from "../../components/Tenders/TenderSummaryTable";

import "./TenderInQuotationPage.css";

// =================================================================================================
// Helpers
// =================================================================================================

/**
 * Builds the namespaced Redux/session key for the current tab.
 *
 * @param {string|number|null|undefined} tid - Tab identifier.
 * @returns {string|null} Namespaced key or null when the id is missing.
 */
const stateKeyFrom = (tid) => (tid ? `${tid}::TIQ` : null);

/**
 * Reads a value from a key-value array by key.
 *
 * @param {Array<{key:string,value:any}>} arr - Snapshot array.
 * @param {string} k - Key to retrieve.
 * @returns {any} Stored value or undefined when not found.
 */
const pickKV = (arr, k) => (arr || []).find((x) => x.key === k)?.value;

/**
 * Normalizes common API envelope shapes to a flat array.
 *
 * @param {any} data - API response payload.
 * @returns {Array|any} Normalized array or original object fallback.
 */
const norm = (data) =>
  Array.isArray(data?.Items)
    ? data.Items
    : Array.isArray(data?.items)
      ? data.items
      : Array.isArray(data?.Data)
        ? data.Data
        : Array.isArray(data?.data)
          ? data.data
          : Array.isArray(data)
            ? data
            : data || [];

/**
 * Performs a defensive deep comparison for snapshot persistence.
 *
 * @param {any} a - First value.
 * @param {any} b - Second value.
 * @returns {boolean} True when values are equivalent.
 */
const deepEq = (a, b) => {
  try {
    return JSON.stringify(a) === JSON.stringify(b);
  } catch {
    return a === b;
  }
};

const QUOTES_PAGE_SIZE = 200;
const LANES_PAGE_SIZE_FLAT = 200;
const SUMMARY_PAGE_SIZE = 200;
const ALL_LANES_PAGE_SIZE = 200;
const DISTINCT_CARRIERS_PAGE_SIZE = 200;

/**
 * Reads TenderPhaseId from a quote payload regardless of casing.
 *
 * @param {object} q - Quote payload.
 * @returns {number|null|undefined} Phase id.
 */
const getQuotePhaseId = (q) => q?.TenderPhaseId ?? q?.tenderPhaseId;

/**
 * Reads QuoteId from a quote payload regardless of casing.
 *
 * @param {object} q - Quote payload.
 * @returns {number|null} Quote id.
 */
const getQuoteId = (q) => q?.QuoteId ?? q?.quoteId ?? q?.Id ?? q?.id ?? null;

/**
 * Reads LaneId from a lane payload regardless of casing.
 *
 * @param {object} lane - Lane payload.
 * @returns {number|null} Lane id.
 */
const getLaneId = (lane) =>
  lane?.LaneId ?? lane?.laneId ?? lane?.Id ?? lane?.id ?? null;

/**
 * Reads the lane origin label from a lane payload regardless of casing.
 *
 * @param {object} lane - Lane payload.
 * @returns {string} Origin label.
 */
const getLaneOrigin = (lane) =>
  lane?.LaneSource ??
  lane?.laneSource ??
  lane?.Origin ??
  lane?.origin ??
  lane?.sourceLocation ??
  lane?.SourceLocation ??
  "";

/**
 * Reads the lane destination label from a lane payload regardless of casing.
 *
 * @param {object} lane - Lane payload.
 * @returns {string} Destination label.
 */
const getLaneDestination = (lane) =>
  lane?.LaneDestination ??
  lane?.laneDestination ??
  lane?.Destination ??
  lane?.destination ??
  lane?.destinationLocation ??
  lane?.DestinationLocation ??
  "";

/**
 * Reads the satisfied phase id from a summary payload regardless of casing.
 *
 * @param {object} r - Summary row payload.
 * @returns {number|null} Satisfied phase id.
 */
const getSummarySatisfiedPhaseId = (r) =>
  r?.satisfiedInTenderPhaseId ?? r?.SatisfiedInTenderPhaseId ?? null;

/**
 * Reads the satisfied timestamp from a summary payload regardless of casing.
 *
 * @param {object} r - Summary row payload.
 * @returns {string|null} Satisfied timestamp.
 */
const getSummarySatisfiedAt = (r) => r?.satisfiedAt ?? r?.SatisfiedAt ?? null;

const ssKey = (ns) => `NS_SNAPSHOT:${ns}`;

/**
 * Reads a persisted namespace snapshot from sessionStorage.
 *
 * @param {string|null} ns - Namespace key.
 * @returns {Array|null} Parsed snapshot array or null when unavailable.
 */
const readFromStorage = (ns) => {
  if (!ns) return null;

  try {
    const raw = sessionStorage.getItem(ssKey(ns));
    if (!raw) return null;

    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : null;
  } catch {
    return null;
  }
};

/**
 * Persists a namespace snapshot into sessionStorage.
 *
 * @param {string|null} ns - Namespace key.
 * @param {Array} snapshotArray - Snapshot entries.
 * @returns {void}
 */
const writeToStorage = (ns, snapshotArray) => {
  if (!ns) return;

  try {
    sessionStorage.setItem(
      ssKey(ns),
      JSON.stringify(Array.isArray(snapshotArray) ? snapshotArray : [])
    );
  } catch {
    // Storage can be unavailable or full. Redux remains the primary source of truth.
  }
};

/**
 * Resolves the real last phase id from the phase chain.
 *
 * @param {Array} phasesList - Tender phases.
 * @returns {number|null} Last phase id.
 */
const resolveLastPhaseId = (phasesList) => {
  const list = Array.isArray(phasesList) ? phasesList : [];
  if (!list.length) return null;

  const lastIds = list
    .map((p) => p?.LastTenderPhaseId ?? p?.lastTenderPhaseId ?? null)
    .filter((x) => x != null);

  const last =
    list.find((p) => !lastIds.includes(p.Id)) || list[list.length - 1];

  return last?.Id ?? null;
};

/**
 * Flattens the last-phase flag onto quotes so downstream components do not need
 * to rely on nested TenderPhase payloads.
 *
 * @param {Array} quotesList - Quote list.
 * @param {number|null} lastPhaseId - Real last phase id.
 * @returns {Array} Enriched quotes.
 */
const withIsLastPhase = (quotesList, lastPhaseId) => {
  const list = Array.isArray(quotesList) ? quotesList : [];
  if (!lastPhaseId) return list;

  return list.map((q) => {
    const qPhaseId = q?.TenderPhaseId ?? q?.tenderPhaseId ?? null;
    const isLast = qPhaseId != null && Number(qPhaseId) === Number(lastPhaseId);

    return {
      ...q,
      IsLastPhase: isLast,
      isLastPhase: isLast,
    };
  });
};

// =================================================================================================
// Snapshot keys
// =================================================================================================

/**
 * Stores only lightweight UI state and lightweight data needed to reconstruct
 * the page navigation context.
 *
 * Heavy datasets such as quotes, lanes and aggregate collections are reloaded
 * from the backend when the tab is rebuilt.
 */
const K = {
  owner: "TIQ.ownerTabId",
  operation: "TIQ.operation",
  sessionLine: "TIQ.sessionLine",
  activeView: "TIQ.activeView",
  selectedCarrierId: "TIQ.selectedCarrierId",
  selectedPhaseId: "TIQ.selectedPhaseId",
  detailPhaseId: "TIQ.detailPhaseId",
  bestMetric: "TIQ.bestMetric",
  tenderInfo: "TIQ.tenderInfo",
  phases: "TIQ.phases",
  lastSyncAt: "TIQ.lastSyncAt",
  tenderSchemaOptions: "TIQ.tenderSchemaOptions",
  tenderFkData: "TIQ.tenderFkData",
};

// =================================================================================================
// Component
// =================================================================================================

/**
 * Page responsible for the Tender "In Quotation" backoffice workflow.
 *
 * Responsibilities:
 * - Load Tender context and related datasets.
 * - Orchestrate all inline views.
 * - Keep lightweight per-tab UI state persisted.
 * - Rebuild heavy data from the backend when required.
 *
 * @param {object} props - Component props.
 * @param {Function} props.handleTabClose - Callback used to close the current tab.
 * @param {Function} props.toastMessages - Optional toast dispatcher override.
 * @returns {JSX.Element} Page component.
 */
export default function TenderInQuotationPage({
  handleTabClose,
  toastMessages,
}) {
  const { t } = useTranslation();
  const ctx = useContext(LoadingContext);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { renameTab } = useContext(NavigateContext);

  // ===============================================================================================
  // Redux state
  // ===============================================================================================

  const selectedTab = useSelector(
    (s) => s.StateKeyValue.find((o) => o.key === "selectedTab")?.value
  );
  const tabsList = useSelector(
    (s) => s.StateKeyValue.find((o) => o.key === "tabs")?.value
  );
  const globalOperation = useSelector(
    (s) => s.StateKeyValue.find((o) => o.key === "operation")?.value
  );
  const globalLine = useSelector(
    (s) => s.StateKeyValue.find((o) => o.key === "TenderLine")?.value
  );
  const stateKeyValue = useSelector((s) => s.StateKeyValue);

  const allStateKeys = useMemo(
    () => (stateKeyValue || []).map((o) => o.key),
    [stateKeyValue]
  );

  const currentNsKey = stateKeyFrom(selectedTab);
  const currentNsSnapshot = useSelector((s) =>
    currentNsKey
      ? s.StateKeyValue.find((o) => o.key === currentNsKey)?.value
      : undefined
  );

  // ===============================================================================================
  // Local UI state
  // ===============================================================================================

  const [id, setId] = useState(selectedTab);

  useEffect(() => {
    setId(selectedTab);
  }, [selectedTab]);

  const [operation, setOperation] = useState(globalOperation ?? "View");
  const [sessionLine, setSessionLine] = useState(globalLine ?? null);
  const [activeView, setActiveView] = useState("inQuotation");

  const [selectedCarrierId, setSelectedCarrierId] = useState(null);
  const [selectedPhaseId, setSelectedPhaseId] = useState(null);
  const [detailPhaseId, setDetailPhaseId] = useState(null);
  const [bestMetric, setBestMetric] = useState("bid");
  const [isFirst, setIsFirst] = useState(true);
  const [lastSyncAt, setLastSyncAt] = useState(null);

  // ===============================================================================================
  // Toast and loading refs
  // ===============================================================================================

  const toastRef = useRef(toastMessages || ctx.toastMessages);
  const setLoadRef = useRef(ctx.setIsLoad);
  const loadCountRef = useRef(0);
  const lastSyncAtRef = useRef(null);

  useEffect(() => {
    setLoadRef.current = ctx.setIsLoad;
  }, [ctx.setIsLoad]);

  useEffect(() => {
    toastRef.current = toastMessages || ctx.toastMessages;
  }, [toastMessages, ctx.toastMessages]);

  useEffect(() => {
    lastSyncAtRef.current = lastSyncAt;
  }, [lastSyncAt]);

  /**
   * Increments the global page loading counter.
   *
   * @returns {void}
   */
  const incLoad = useCallback(() => {
    const prev = loadCountRef.current;
    const next = prev + 1;
    loadCountRef.current = next;

    if (prev === 0) setLoadRef.current?.(true);
  }, []);

  /**
   * Decrements the global page loading counter.
   *
   * @returns {void}
   */
  const decLoad = useCallback(() => {
    const prev = loadCountRef.current;
    const next = Math.max(0, prev - 1);
    loadCountRef.current = next;

    if (prev === 1 && next === 0) setLoadRef.current?.(false);
  }, []);

  // ===============================================================================================
  // Services
  // ===============================================================================================

  const baseUrl = ctx.DEFAULTURL;

  const tenderService = useMemo(
    () => new BufferService("Tenders", baseUrl),
    [baseUrl]
  );
  const tenderServiceUpdate = useMemo(
    () => new BufferService("Tenders/update", baseUrl),
    [baseUrl]
  );
  const phasesService = useMemo(
    () => new BufferService("TenderPhases", baseUrl),
    [baseUrl]
  );
  const tenderQuotesService = useMemo(
    () => new BufferService("TenderQuotes", baseUrl),
    [baseUrl]
  );
  const laneSummariesService = useMemo(
    () => new BufferService("TenderQuotes/laneSummaries", baseUrl),
    [baseUrl]
  );
  const laneSummariesAcceptedService = useMemo(
    () => new BufferService("TenderQuotes/laneSummaries/accepted", baseUrl),
    [baseUrl]
  );
  const distinctCarriersService = useMemo(
    () => new BufferService("TenderQuotes/distinctCarriers", baseUrl),
    [baseUrl]
  );
  const tenderQuotesUpdateService = useMemo(
    () => new BufferService("TenderQuotes/update", baseUrl),
    [baseUrl]
  );
  const tenderLanesFlatService = useMemo(
    () => new BufferService("TenderLanes/flat", baseUrl),
    [baseUrl]
  );
  const tenderLanesSummaryService = useMemo(
    () => new BufferService("TenderLanes", baseUrl),
    [baseUrl]
  );
  const phaseCarriersService = useMemo(
    () => new BufferService("TenderPhases", baseUrl),
    [baseUrl]
  );
  const tenderOptionsService = useMemo(
    () => new BufferService("Tenders", baseUrl),
    [baseUrl]
  );

  // ===============================================================================================
  // Loaded datasets
  // ===============================================================================================

  const [tenderInfo, setTenderInfo] = useState({});
  const [phases, setPhases] = useState([]);

  const [quotes, setQuotes] = useState([]);
  const [quotesAccepted, setQuotesAccepted] = useState([]);
  // eslint-disable-next-line no-unused-vars
  const [quotesUnaccepted, setQuotesUnaccepted] = useState([]);

  const [lanes, setLanes] = useState([]);
  const [allLanes, setAllLanes] = useState([]);
  const [carriers, setCarriers] = useState([]);
  const [distinctCarriersItems, setDistinctCarriersItems] = useState([]);
  const [summaryRows, setSummaryRows] = useState([]);
  const [laneSummariesRows, setLaneSummariesRows] = useState([]);
  const [laneSummariesAcceptedRows, setLaneSummariesAcceptedRows] = useState([]);

  const [refreshKey, setRefreshKey] = useState(0);
  const [distinctCarriersReloadKey, setDistinctCarriersReloadKey] = useState(0);

  const [tenderSchemaOptions, setTenderSchemaOptions] = useState([]);
  const [tenderFkData, setTenderFkData] = useState({});

  const [acceptBusyIds, setAcceptBusyIds] = useState(() => new Set());
  const acceptBusyIdsRef = useRef(acceptBusyIds);

  /**
   * Reads the current accepted flag from a quote regardless of casing.
   *
   * @param {object} q - Quote payload.
   * @returns {boolean} Accepted flag.
   */
  const getQuoteAccepted = (q) => {
    if (!q) return false;
    return Boolean(q.IsAccepted ?? q.isAccepted ?? false);
  };

  useEffect(() => {
    acceptBusyIdsRef.current = acceptBusyIds;
  }, [acceptBusyIds]);

  // ===============================================================================================
  // Snapshot infrastructure
  // ===============================================================================================

  const activeNsRef = useRef(null);
  const activeTidRef = useRef(null);
  const prevTabRef = useRef(null);
  const persistDebounceRef = useRef(null);
  const didGCRef = useRef(false);
  const hydratedOnceRef = useRef(false);
  const nsSnapshotRef = useRef([]);
  const rehydratingRef = useRef(false);

  useEffect(() => {
    if (currentNsKey && activeNsRef.current === currentNsKey) {
      nsSnapshotRef.current = Array.isArray(currentNsSnapshot)
        ? currentNsSnapshot
        : [];
    }
  }, [currentNsKey, currentNsSnapshot]);

  /**
   * Writes a partial set of entries into the current tab namespace.
   *
   * @param {string|null} nsKey - Namespace key.
   * @param {Array<{key:string,value:any}>} entries - Entries to merge.
   * @returns {void}
   */
  const setSession = useCallback(
    (nsKey, entries) => {
      if (!nsKey) return;

      const current =
        nsKey === activeNsRef.current ? nsSnapshotRef.current || [] : [];
      const map = new Map(current.map((e) => [e.key, e]));
      let changed = false;

      for (const kv of entries) {
        const prevVal = map.get(kv.key)?.value;
        if (!deepEq(prevVal, kv.value)) {
          changed = true;
          map.set(kv.key, kv);
        }
      }

      if (changed) {
        const nextArray = Array.from(map.values());
        dispatch(M_StateActions.addM_State({ key: nsKey, value: nextArray }));
        writeToStorage(nsKey, nextArray);

        if (nsKey === activeNsRef.current) nsSnapshotRef.current = nextArray;
      }
    },
    [dispatch]
  );

  /**
   * Persists only lightweight tab state.
   *
   * Heavy collections are intentionally excluded so the snapshot does not grow
   * with thousands of quotes, lanes or aggregate rows.
   *
   * @param {string|number|null} forTabId - Optional explicit tab id.
   * @returns {void}
   */
  const flushSnapshot = useCallback(
    (forTabId = null) => {
      const tid = forTabId ?? activeTidRef.current ?? id;
      const ns = stateKeyFrom(tid);
      if (!ns) return;

      const payload = [
        { key: K.owner, value: String(tid) },
        { key: K.operation, value: operation },
        { key: K.sessionLine, value: sessionLine },
        { key: K.activeView, value: activeView },
        { key: K.selectedCarrierId, value: selectedCarrierId },
        { key: K.selectedPhaseId, value: selectedPhaseId },
        { key: K.detailPhaseId, value: detailPhaseId },
        { key: K.bestMetric, value: bestMetric },
        { key: K.tenderInfo, value: tenderInfo || {} },
        { key: K.phases, value: phases || [] },
        { key: K.lastSyncAt, value: lastSyncAt },
        { key: K.tenderSchemaOptions, value: tenderSchemaOptions || [] },
        { key: K.tenderFkData, value: tenderFkData || {} },
      ];

      setSession(ns, payload);
    },
    [
      id,
      operation,
      sessionLine,
      activeView,
      selectedCarrierId,
      selectedPhaseId,
      detailPhaseId,
      bestMetric,
      tenderInfo,
      phases,
      lastSyncAt,
      tenderSchemaOptions,
      tenderFkData,
      setSession,
    ]
  );

  useEffect(() => {
    if (didGCRef.current) return;
    if (!tabsList || tabsList.length === 0) return;

    didGCRef.current = true;

    try {
      const openIds = new Set((tabsList || []).map((t) => String(t.value)));

      (allStateKeys || [])
        .filter((k) => typeof k === "string" && k.endsWith("::TIQ"))
        .forEach((ns) => {
          const tid = ns.split("::")[0];

          if (!openIds.has(String(tid))) {
            dispatch(M_StateActions.removeM_State({ key: ns }));
            sessionStorage.removeItem(ssKey(ns));
          }
        });
    } catch {
      // No action is required when cleanup fails.
    }
  }, [tabsList, allStateKeys, dispatch]);

  /**
   * Clears heavy in-memory datasets so a restored tab always reconstructs them
   * from the backend instead of reusing stale arrays from a previous tab instance.
   *
   * @returns {void}
   */
  const clearHeavyState = useCallback(() => {
    setQuotes([]);
    setQuotesAccepted([]);
    setQuotesUnaccepted([]);
    setLanes([]);
    setAllLanes([]);
    setCarriers([]);
    setDistinctCarriersItems([]);
    setSummaryRows([]);
    setLaneSummariesRows([]);
    setLaneSummariesAcceptedRows([]);
  }, []);

  useLayoutEffect(() => {
    const prev = prevTabRef.current;
    if (prev !== null && prev !== selectedTab) flushSnapshot(prev);

    if (!selectedTab) {
      prevTabRef.current = null;
      return;
    }

    const ns = stateKeyFrom(selectedTab);
    activeNsRef.current = ns;
    activeTidRef.current = selectedTab;

    let snap = Array.isArray(currentNsSnapshot) ? currentNsSnapshot : [];

    if (!snap.length) {
      const fromStore = readFromStorage(ns);

      if (Array.isArray(fromStore) && fromStore.length) {
        snap = fromStore;
        dispatch(M_StateActions.addM_State({ key: ns, value: snap }));
      }
    }

    rehydratingRef.current = true;

    const storedOwner = pickKV(snap, K.owner);
    const storedOp = pickKV(snap, K.operation);
    const storedLine = pickKV(snap, K.sessionLine);
    const storedView = pickKV(snap, K.activeView);
    const storedCar = pickKV(snap, K.selectedCarrierId);
    const storedPhase = pickKV(snap, K.selectedPhaseId);
    const storedDetPh = pickKV(snap, K.detailPhaseId);
    const storedBestMetric = pickKV(snap, K.bestMetric);
    const snapTender = pickKV(snap, K.tenderInfo);
    const snapPhases = pickKV(snap, K.phases);
    const snapLastSyncAt = pickKV(snap, K.lastSyncAt);
    const snapTenderSchema = pickKV(snap, K.tenderSchemaOptions);
    const snapTenderFK = pickKV(snap, K.tenderFkData);

    clearHeavyState();

    if (storedOwner) {
      if (storedOp !== undefined) setOperation(storedOp);
      if (storedLine !== undefined) setSessionLine(storedLine);
      if (storedView !== undefined) setActiveView(storedView);
      if (storedCar !== undefined) setSelectedCarrierId(storedCar);
      if (storedPhase !== undefined) setSelectedPhaseId(storedPhase);
      if (storedDetPh !== undefined) setDetailPhaseId(storedDetPh);
      if (storedBestMetric !== undefined) setBestMetric(storedBestMetric || "bid");
      if (snapTender) setTenderInfo(snapTender);
      if (Array.isArray(snapPhases)) setPhases(snapPhases);
      if (snapLastSyncAt !== undefined) setLastSyncAt(snapLastSyncAt);
      if (Array.isArray(snapTenderSchema)) setTenderSchemaOptions(snapTenderSchema);
      if (snapTenderFK && typeof snapTenderFK === "object") setTenderFkData(snapTenderFK);

      hydratedOnceRef.current = true;
      nsSnapshotRef.current = snap;
    } else {
      const seed = [
        { key: K.owner, value: String(selectedTab) },
        { key: K.operation, value: operation },
        { key: K.sessionLine, value: sessionLine },
        { key: K.activeView, value: activeView },
        { key: K.selectedCarrierId, value: selectedCarrierId },
        { key: K.selectedPhaseId, value: selectedPhaseId },
        { key: K.detailPhaseId, value: detailPhaseId },
        { key: K.bestMetric, value: bestMetric },
        { key: K.tenderInfo, value: {} },
        { key: K.phases, value: [] },
        { key: K.lastSyncAt, value: null },
        { key: K.tenderSchemaOptions, value: [] },
        { key: K.tenderFkData, value: {} },
      ];

      dispatch(M_StateActions.addM_State({ key: ns, value: seed }));
      writeToStorage(ns, seed);
      nsSnapshotRef.current = seed;
      hydratedOnceRef.current = false;
    }

    setTimeout(() => {
      rehydratingRef.current = false;
    }, 0);

    prevTabRef.current = selectedTab || null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTab]);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        if ((tenderSchemaOptions || []).length) return;

        const opts = await tenderOptionsService.options?.();
        if (cancelled) return;

        if (!isFailurePayload(opts)) {
          setTenderSchemaOptions(Array.isArray(opts) ? opts : []);
        } else {
          setTenderSchemaOptions([]);
        }

        setTenderFkData({});
      } catch {
        setTenderSchemaOptions([]);
        setTenderFkData({});
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [activeView, tenderOptionsService, tenderSchemaOptions]);

  useEffect(() => {
    const handleBeforeUnload = () => {
      if (persistDebounceRef.current) clearTimeout(persistDebounceRef.current);
      flushSnapshot();
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);

      if (persistDebounceRef.current) clearTimeout(persistDebounceRef.current);
      flushSnapshot();
    };
  }, [flushSnapshot]);

  useEffect(() => {
    if (!activeNsRef.current || rehydratingRef.current) return;

    if (persistDebounceRef.current) clearTimeout(persistDebounceRef.current);
    persistDebounceRef.current = setTimeout(() => flushSnapshot(), 200);

    return () => {
      if (persistDebounceRef.current) clearTimeout(persistDebounceRef.current);
    };
  }, [
    operation,
    sessionLine,
    activeView,
    selectedCarrierId,
    selectedPhaseId,
    detailPhaseId,
    bestMetric,
    tenderInfo,
    phases,
    lastSyncAt,
    tenderSchemaOptions,
    tenderFkData,
    flushSnapshot,
  ]);

  useEffect(() => {
    if (!isFirst) return;

    setIsFirst(false);
    dispatch(M_StateActions.removeM_State({ key: "operation" }));
    dispatch(M_StateActions.removeM_State({ key: "TenderLine" }));

    const ns = stateKeyFrom(id);
    if (!ns) return;

    const seed = [
      { key: K.owner, value: String(id) },
      { key: K.operation, value: operation },
      { key: K.sessionLine, value: sessionLine },
      { key: K.activeView, value: activeView },
      { key: K.selectedCarrierId, value: selectedCarrierId },
      { key: K.selectedPhaseId, value: selectedPhaseId },
      { key: K.detailPhaseId, value: detailPhaseId },
      { key: K.bestMetric, value: bestMetric },
      { key: K.tenderInfo, value: tenderInfo },
      { key: K.phases, value: phases },
      { key: K.lastSyncAt, value: lastSyncAt },
      { key: K.tenderSchemaOptions, value: tenderSchemaOptions },
      { key: K.tenderFkData, value: tenderFkData },
    ];

    dispatch(M_StateActions.addM_State({ key: ns, value: seed }));
    writeToStorage(ns, seed);
    nsSnapshotRef.current = seed;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFirst]);

  // ===============================================================================================
  // Force heavy reload when the tab changes
  // ===============================================================================================

  useEffect(() => {
    if (!selectedTab) return;
    setRefreshKey((prev) => prev + 1);
    setDistinctCarriersReloadKey((prev) => prev + 1);
  }, [selectedTab]);

  // ===============================================================================================
  // Tab title
  // ===============================================================================================

  useEffect(() => {
    if (!renameTab) return;

    const ownerId =
      pickKV(currentNsSnapshot, K.owner) || activeTidRef.current || id;
    if (!ownerId) return;

    const base = t("TenderInQuotation");
    const viewKey =
      activeView === "carrier"
        ? t("CarrierDetails")
        : activeView === "lane"
          ? t("Lane")
          : activeView === "summary"
            ? t("Summary") || "Summary"
            : activeView === "inQuotation"
              ? t("InQuotation")
              : t("Details");

    const snapshotName = pickKV(currentNsSnapshot, K.tenderInfo)?.Name;
    const name = (tenderInfo?.Name || snapshotName || "").trim();

    const label = name
      ? `${base} — ${name} (${viewKey})`
      : `${base} (${viewKey})`;

    renameTab(String(ownerId), label);
  }, [activeView, tenderInfo?.Name, currentNsSnapshot, id, renameTab, t]);

  // ===============================================================================================
  // Main data loader
  // ===============================================================================================

  useEffect(() => {
    let cancelled = false;
    const lineId = sessionLine?.Id;
    if (!lineId) return;

    const hasMinimumHeavyData =
      Array.isArray(quotes) &&
      Array.isArray(allLanes) &&
      Array.isArray(summaryRows) &&
      quotes.length > 0 &&
      allLanes.length > 0 &&
      summaryRows.length > 0;

    const hasHydratedData =
      hydratedOnceRef.current &&
      Number(tenderInfo?.Id) === Number(lineId) &&
      hasMinimumHeavyData;

    const SYNC_MAX_AGE_MS = 30_000;
    const lastSync = Number(lastSyncAtRef.current);
    const isFreshEnough =
      Number.isFinite(lastSync) && Date.now() - lastSync < SYNC_MAX_AGE_MS;

    if (hasHydratedData && refreshKey === 0 && isFreshEnough) return;

    /**
     * Loads phases and carrier universe for the current tender.
     *
     * @param {object} args - Loader arguments.
     * @param {object} args.tender - Tender payload.
     * @param {number} args.lineId - Tender id.
     * @param {boolean} args.isSpot - Spot flag.
     * @returns {Promise<Array>} Loaded phases.
     */
    const loadPhasesPaged = async ({ tender, lineId, isSpot }) => {
      let phasesList = Array.isArray(tender?.Phases) ? tender.Phases : [];

      if (!isSpot && phasesList.length === 0) {
        const pageSize = 20;
        let page = 1;
        const acc = [];
        const seen = new Set();
        const filter = encodeURIComponent(
          `TenderId eq ${lineId} and Inactive eq false`
        );

        while (true) {
          if (cancelled) break;

          const res = await phasesService.filter(
            `?Page=${page}&PageSize=${pageSize}&filter=${filter}`
          );

          if (cancelled) break;
          if (isFailurePayload(res)) break;

          const items = norm(res);
          if (!items.length) break;

          for (const it of items) {
            const pid = it?.Id;
            if (pid != null && !seen.has(pid)) {
              seen.add(pid);
              acc.push(it);
            }
          }

          const totalPages = Number(
            res?.TotalPages ??
            res?.totalPages ??
            (items.length < pageSize ? page : page + 1)
          );

          if (page >= totalPages) break;
          page += 1;
        }

        phasesList = acc;
      }

      if (cancelled) return phasesList;
      setPhases(phasesList);

      if (!isSpot && phasesList.length > 0 && selectedPhaseId == null) {
        const lastIds = phasesList
          .map((p) => p.LastTenderPhaseId)
          .filter((pid) => pid != null);

        const mostRecent =
          phasesList.find((p) => !lastIds.includes(p.Id)) ||
          phasesList[phasesList.length - 1];

        setSelectedPhaseId(mostRecent?.Id ?? null);

        if (detailPhaseId == null) {
          setDetailPhaseId(mostRecent?.Id ?? null);
        }
      }

      if (!isSpot) {
        const results = await Promise.all(
          (phasesList || []).map(async (p) => {
            const res = await phaseCarriersService.getId(`${p.Id}/Carriers`);
            if (isFailurePayload(res)) return [];
            return norm(res);
          })
        );

        if (!cancelled) {
          const all = results.flat();
          const unique = [
            ...new Map(
              all.map((c) => [
                c.CarrierId || c.Id,
                {
                  CarrierId: c.CarrierId || c.Id,
                  CarrierName: c.CarrierName || c.Name,
                  Name: c.CarrierName || c.Name,
                },
              ])
            ).values(),
          ];

          setCarriers(unique);
        }
      } else {
        const spotCarriers = Array.isArray(tender?.Carriers)
          ? tender.Carriers
          : [];

        const mapped = spotCarriers
          .map((c) => {
            const carrierObj = c?.Carrier || {};

            return {
              CarrierId: c?.CarrierId || carrierObj?.Id || c?.Id,
              CarrierName: c?.CarrierName || carrierObj?.Name || "",
              Name: c?.CarrierName || carrierObj?.Name || "",
            };
          })
          .filter((x) => x.CarrierId != null);

        if (!cancelled) setCarriers(mapped);
      }

      return phasesList;
    };

    /**
     * Loads accepted and unaccepted quotes for the tender.
     *
     * @param {object} args - Loader arguments.
     * @param {number} args.lineId - Tender id.
     * @returns {Promise<object>} Loaded quote collections.
     */
    const loadQuotesByAcceptancePaged = async ({ lineId }) => {
      const pageSize = QUOTES_PAGE_SIZE;
      let page = 1;

      const acceptedAcc = [];
      const unacceptedAcc = [];
      const seen = new Set();

      while (true) {
        if (cancelled) break;

        const filterAccepted = encodeURIComponent(
          `Inactive eq false and TenderId eq ${lineId} and IsAccepted eq true`
        );
        const filterUnaccepted = encodeURIComponent(
          `Inactive eq false and TenderId eq ${lineId} and IsAccepted eq false`
        );

        const [resA, resB] = await Promise.all([
          tenderQuotesService.filter(
            `?Page=${page}&PageSize=${pageSize}&filter=${filterAccepted}`
          ),
          tenderQuotesService.filter(
            `?Page=${page}&PageSize=${pageSize}&filter=${filterUnaccepted}`
          ),
        ]);

        if (cancelled) break;

        const itemsA = isFailurePayload(resA) ? [] : norm(resA);
        const itemsB = isFailurePayload(resB) ? [] : norm(resB);

        for (const it of itemsA) {
          const qid = getQuoteId(it);
          if (qid != null && !seen.has(qid)) {
            seen.add(qid);
            acceptedAcc.push(it);
          }
        }

        for (const it of itemsB) {
          const qid = getQuoteId(it);
          if (qid != null && !seen.has(qid)) {
            seen.add(qid);
            unacceptedAcc.push(it);
          }
        }

        const totalPagesA = Number(
          resA?.TotalPages ??
          resA?.totalPages ??
          (itemsA.length < pageSize ? page : page + 1)
        );
        const totalPagesB = Number(
          resB?.TotalPages ??
          resB?.totalPages ??
          (itemsB.length < pageSize ? page : page + 1)
        );

        const totalPages = Math.max(totalPagesA, totalPagesB);
        if (page >= totalPages) break;
        page += 1;
      }

      const allQuotes = [...acceptedAcc, ...unacceptedAcc];

      if (!cancelled) {
        setQuotes(allQuotes);
        setQuotesAccepted(acceptedAcc);
        setQuotesUnaccepted(unacceptedAcc);
      }

      return { allQuotes, acceptedAcc, unacceptedAcc };
    };

    /**
     * Loads persisted TenderLane snapshot rows.
     *
     * These rows provide:
     * - lane universe metadata
     * - satisfied official snapshot metadata
     *
     * They are not used as the primary numeric source for Summary metrics.
     *
     * @param {object} args - Loader arguments.
     * @param {number} args.lineId - Tender id.
     * @returns {Promise<Array>} Loaded TenderLane rows.
     */
    const loadLaneSummaryPaged = async ({ lineId }) => {
      const pageSize = SUMMARY_PAGE_SIZE;
      let page = 1;
      const acc = [];
      const seen = new Set();

      const filter = encodeURIComponent(`TenderId eq ${Number(lineId)}`);

      while (true) {
        if (cancelled) break;

        const res = await tenderLanesSummaryService.filter(
          `?Page=${page}&PageSize=${pageSize}&Filter=${filter}`
        );

        if (cancelled) break;
        if (isFailurePayload(res)) break;

        const items = norm(res);
        if (!items.length) break;

        for (const it of items) {
          const laneId = it?.laneId ?? it?.LaneId ?? it?.LaneID ?? null;
          if (laneId == null) continue;

          const key = String(laneId);
          if (seen.has(key)) continue;
          seen.add(key);

          acc.push(it);
        }

        const totalPages = Number(
          res?.TotalPages ??
          res?.totalPages ??
          (items.length < pageSize ? page : page + 1)
        );

        if (page >= totalPages) break;
        page += 1;
      }

      if (!cancelled) setSummaryRows(acc);
      return acc;
    };

    const run = async () => {
      incLoad();

      try {
        const tender = await tenderService.getId(lineId);
        if (cancelled) return;

        if (isFailurePayload(tender)) {
          toastRef.current?.(
            "error",
            "",
            apiMessage(tender, t("ErrorLoadingData"))
          );
          return;
        }

        setTenderInfo(tender);

        const tenderTypeId = Number(tender?.TenderTypeId ?? 0);
        const tenderTypeName = String(tender?.TenderType ?? "").toLowerCase();
        const isSpot = tenderTypeId === 1 || tenderTypeName === "spot";

        await Promise.allSettled([
          loadPhasesPaged({ tender, lineId, isSpot }),
          loadQuotesByAcceptancePaged({ lineId }),
          loadLaneSummaryPaged({ lineId }),
        ]);

        if (!cancelled) {
          hydratedOnceRef.current = true;
          setLastSyncAt(Date.now());
        }
      } catch (err) {
        if (!cancelled) {
          toastRef.current?.(
            "error",
            "",
            apiMessage(err, t("ErrorLoadingData"))
          );
        }
      } finally {
        decLoad();
      }
    };

    run();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    sessionLine?.Id,
    refreshKey,
    baseUrl,
    tenderInfo?.Id,
    selectedPhaseId,
    phasesService,
    detailPhaseId,
    phaseCarriersService,
    tenderQuotesService,
    tenderLanesSummaryService,
    incLoad,
    tenderService,
    t,
    decLoad
  ]);

  // ===============================================================================================
  // Hydrated fallbacks
  // ===============================================================================================

  const hvTender = useMemo(
    () =>
      Object.keys(tenderInfo || {}).length
        ? tenderInfo
        : pickKV(currentNsSnapshot, K.tenderInfo) || {},
    [tenderInfo, currentNsSnapshot]
  );

  const hvPhases = useMemo(
    () =>
      phases && phases.length
        ? phases
        : pickKV(currentNsSnapshot, K.phases) || [],
    [phases, currentNsSnapshot]
  );

  const hvQuotesAll = useMemo(() => quotes || [], [quotes]);
  const hvQuotesAccepted = useMemo(() => quotesAccepted || [], [quotesAccepted]);
  const hvLanes = useMemo(() => lanes || [], [lanes]);
  const hvAllLanes = useMemo(() => allLanes || [], [allLanes]);
  const hvSummaryRows = useMemo(() => summaryRows || [], [summaryRows]);
  const hvLaneSummaries = useMemo(() => laneSummariesRows || [], [laneSummariesRows]);
  const hvLaneSummariesAccepted = useMemo(
    () => laneSummariesAcceptedRows || [],
    [laneSummariesAcceptedRows]
  );

  // ===============================================================================================
  // Derived state
  // ===============================================================================================

  const isSpotTender =
    Number(hvTender?.TenderTypeId) === 1 ||
    String(hvTender?.TenderType || "").toLowerCase() === "spot";

  const isTenderFinished = useMemo(() => {
    const v = hvTender?.IsFinished ?? hvTender?.isFinished ?? false;
    return v === true;
  }, [hvTender]);

  const lanesPhaseId = useMemo(() => {
    if (isSpotTender) return null;
    return activeView === "carrier" ? detailPhaseId : selectedPhaseId;
  }, [isSpotTender, activeView, detailPhaseId, selectedPhaseId]);

  const lastPhaseId = useMemo(() => {
    if (isSpotTender) return null;
    return resolveLastPhaseId(hvPhases);
  }, [isSpotTender, hvPhases]);

  // ===============================================================================================
  // Lane summaries
  // ===============================================================================================

  useEffect(() => {
    let cancelled = false;

    const tenderId = sessionLine?.Id ?? null;
    if (!tenderId) return;

    const viewPhaseId = isSpotTender ? null : lanesPhaseId;

    /**
     * Fetches all pages for a lane-summary endpoint.
     *
     * @param {object} args - Fetch arguments.
     * @param {object} args.service - BufferService instance.
     * @param {string[]} args.filterParts - Filter clauses.
     * @param {number} args.pageSize - Page size.
     * @returns {Promise<Array>} Aggregated rows.
     */
    const fetchPaged = async ({ service, filterParts, pageSize }) => {
      let page = 1;
      const acc = [];
      const seen = new Set();

      const filter = encodeURIComponent(filterParts.join(" and "));

      while (true) {
        if (cancelled) return [];

        const res = await service.filter(
          `?Page=${page}&PageSize=${pageSize}&Filter=${filter}`
        );

        if (cancelled) return [];
        if (isFailurePayload(res)) return [];

        const items = Array.isArray(res?.Items)
          ? res.Items
          : Array.isArray(res)
            ? res
            : [];

        if (!items.length) break;

        for (const it of items) {
          const laneId = it?.LaneId ?? it?.laneId ?? it?.Id ?? it?.id ?? null;
          if (laneId == null) continue;

          const phaseKey = it?.TenderPhaseId ?? it?.tenderPhaseId ?? "null";
          const key = `${phaseKey}::${laneId}`;
          if (seen.has(key)) continue;
          seen.add(key);
          acc.push(it);
        }

        const totalPages = Number(
          res?.TotalPages ??
          res?.totalPages ??
          (items.length < pageSize ? page : page + 1)
        );

        if (page >= totalPages) break;
        page += 1;
      }

      return acc;
    };

    const run = async () => {
      const shouldShowLoading =
        activeView === "carrier" || activeView === "lane";
      if (shouldShowLoading) incLoad();

      try {
        const commonFilterParts = [
          `TenderId eq ${Number(tenderId)}`,
          "Inactive eq false",
        ];

        const pageSize = 500;

        const allFilterParts = [...commonFilterParts];
        if (viewPhaseId != null) {
          allFilterParts.push(`TenderPhaseId eq ${Number(viewPhaseId)}`);
        }

        const acceptedAllFilterParts = [...commonFilterParts];

        const [allRows, acceptedAllRows] = await Promise.all([
          fetchPaged({
            service: laneSummariesService,
            filterParts: allFilterParts,
            pageSize,
          }),
          fetchPaged({
            service: laneSummariesAcceptedService,
            filterParts: acceptedAllFilterParts,
            pageSize,
          }),
        ]);

        if (cancelled) return;

        setLaneSummariesRows(allRows);
        setLaneSummariesAcceptedRows(acceptedAllRows);
      } catch {
        if (!cancelled) {
          setLaneSummariesRows([]);
          setLaneSummariesAcceptedRows([]);
        }
      } finally {
        if (shouldShowLoading) decLoad();
      }
    };

    run();

    return () => {
      cancelled = true;
    };
  }, [
    sessionLine?.Id,
    isSpotTender,
    lanesPhaseId,
    refreshKey,
    activeView,
    laneSummariesService,
    laneSummariesAcceptedService,
    incLoad,
    decLoad,
  ]);

  // ===============================================================================================
  // Quote enrichment
  // ===============================================================================================

  const hvQuotesAllEnriched = useMemo(
    () => withIsLastPhase(hvQuotesAll, lastPhaseId),
    [hvQuotesAll, lastPhaseId]
  );

  const hvQuotesAcceptedEnriched = useMemo(
    () => withIsLastPhase(hvQuotesAccepted, lastPhaseId),
    [hvQuotesAccepted, lastPhaseId]
  );

  // ===============================================================================================
  // Best lane metrics for lane and carrier views
  // ===============================================================================================

  const laneBestMap = useMemo(() => {
    const map = new Map();

    for (const r of Array.isArray(hvLaneSummariesAccepted)
      ? hvLaneSummariesAccepted
      : []) {
      const laneIdRaw = r?.LaneId ?? r?.laneId ?? r?.Id ?? r?.id ?? null;
      const laneId = laneIdRaw != null ? Number(laneIdRaw) : null;
      if (!Number.isFinite(laneId)) continue;

      const current = map.get(laneId) || {
        bestBidValue: null,
        bestLoadsValue: null,
        bestEtdValue: null,
      };

      const minBid = Number(r?.MinBidValue ?? r?.minBidValue);
      const maxLoads = Number(r?.MaxLoads ?? r?.maxLoads);
      const minEtd = Number(
        r?.MinEstimatedTransitDays ?? r?.minEstimatedTransitDays
      );

      if (Number.isFinite(minBid)) {
        current.bestBidValue =
          current.bestBidValue == null
            ? minBid
            : Math.min(current.bestBidValue, minBid);
      }

      if (Number.isFinite(maxLoads)) {
        current.bestLoadsValue =
          current.bestLoadsValue == null
            ? maxLoads
            : Math.max(current.bestLoadsValue, maxLoads);
      }

      if (Number.isFinite(minEtd)) {
        current.bestEtdValue =
          current.bestEtdValue == null
            ? minEtd
            : Math.min(current.bestEtdValue, minEtd);
      }

      map.set(laneId, current);
    }

    return map;
  }, [hvLaneSummariesAccepted]);

  // ===============================================================================================
  // Distinct carriers with quotes
  // ===============================================================================================

  useEffect(() => {
    let cancelled = false;

    const tenderId = sessionLine?.Id;
    if (!tenderId) {
      setDistinctCarriersItems([]);
      return;
    }

    const phaseId =
      isSpotTender
        ? null
        : activeView === "carrier"
          ? detailPhaseId
          : selectedPhaseId;

    const fetchDistinctCarriers = async () => {
      const shouldShowLoading = activeView === "carrier";
      if (shouldShowLoading) incLoad();

      try {
        const filterParts = [`TenderId eq ${tenderId}`, `Inactive eq false`];
        if (phaseId != null) filterParts.push(`TenderPhaseId eq ${phaseId}`);

        const filter = encodeURIComponent(filterParts.join(" and "));
        const pageSize = DISTINCT_CARRIERS_PAGE_SIZE;
        let page = 1;

        const acc = [];
        const seen = new Set();

        while (true) {
          if (cancelled) return;

          const res = await distinctCarriersService.filter(
            `?Page=${page}&PageSize=${pageSize}&filter=${filter}`
          );

          if (cancelled) return;
          if (isFailurePayload(res)) break;

          const items =
            res?.Items ??
            res?.items ??
            res?.Data?.Items ??
            res?.data?.items ??
            [];

          if (!Array.isArray(items) || items.length === 0) break;

          for (const row of items) {
            const carrier = row?.Carrier ?? row?.carrier ?? row ?? {};
            const carrierId = carrier?.CarrierId ?? carrier?.Id ?? null;
            if (carrierId == null) continue;

            const key = String(carrierId);
            if (seen.has(key)) continue;
            seen.add(key);

            acc.push(row);
          }

          const totalPages = Number(
            res?.TotalPages ??
            res?.totalPages ??
            (items.length < pageSize ? page : page + 1)
          );

          if (page >= totalPages) break;
          page += 1;
        }

        setDistinctCarriersItems(acc);
      } finally {
        if (shouldShowLoading) decLoad();
      }
    };

    fetchDistinctCarriers().catch(() => {
      if (!cancelled) setDistinctCarriersItems([]);
    });

    return () => {
      cancelled = true;
    };
  }, [
    sessionLine?.Id,
    isSpotTender,
    activeView,
    selectedPhaseId,
    detailPhaseId,
    refreshKey,
    distinctCarriersReloadKey,
    distinctCarriersService,
    incLoad,
    decLoad,
  ]);

  // ===============================================================================================
  // Lanes loaders
  // ===============================================================================================

  /**
   * Loads current lanes for the active phase context.
   *
   * @param {object} args - Loader arguments.
   * @param {number} args.lineId - Tender id.
   * @param {boolean} args.isSpot - Spot flag.
   * @param {number|null} args.phaseId - Phase id for Non-Spot.
   * @returns {Promise<Array>} Loaded lanes.
   */
  const loadLanesPaged = useCallback(
    async ({ lineId, isSpot, phaseId }) => {
      const pageSize = LANES_PAGE_SIZE_FLAT;
      let page = 1;
      const acc = [];
      const seen = new Set();

      const baseFilter = isSpot
        ? `TenderId eq ${Number(lineId)}`
        : `TenderId eq ${Number(
          lineId
        )} and SatisfiedInTenderPhaseId eq ${Number(phaseId)}`;

      const qFilter = encodeURIComponent(baseFilter);

      while (true) {
        const lanesRes = await tenderLanesFlatService.filter(
          `?Page=${page}&PageSize=${pageSize}&Filter=${qFilter}`
        );

        if (isFailurePayload(lanesRes)) break;

        const items = norm(lanesRes);
        if (!items.length) break;

        for (const it of items) {
          const laneId = it?.laneId ?? it?.LaneId ?? null;
          if (laneId == null || seen.has(laneId)) continue;

          seen.add(laneId);

          const origin = String(
            it?.sourceLocation ?? it?.SourceLocation ?? ""
          ).trim();
          const destination = String(
            it?.destinationLocation ?? it?.DestinationLocation ?? ""
          ).trim();

          acc.push({
            ...it,
            LaneId: Number(laneId),
            Origin: origin,
            Destination: destination,
            LaneSource: origin,
            LaneDestination: destination,
            IsActive:
              it?.inactive != null
                ? !it.inactive
                : it?.Inactive != null
                  ? !it.Inactive
                  : true,
          });
        }

        const totalPages = Number(
          lanesRes?.TotalPages ??
          lanesRes?.totalPages ??
          (items.length < pageSize ? page : page + 1)
        );

        if (page >= totalPages) break;
        page += 1;
      }

      setLanes(acc);
      return acc;
    },
    [tenderLanesFlatService]
  );

  /**
   * Loads the full TenderLane universe for the tender.
   *
   * @param {object} args - Loader arguments.
   * @param {number} args.lineId - Tender id.
   * @returns {Promise<Array>} Loaded lane universe.
   */
  const loadAllLanesPaged = useCallback(
    async ({ lineId }) => {
      const pageSize = ALL_LANES_PAGE_SIZE;
      let page = 1;
      const acc = [];
      const seen = new Set();

      const filter = encodeURIComponent(`TenderId eq ${Number(lineId)}`);

      while (true) {
        const res = await tenderLanesSummaryService.filter(
          `?Page=${page}&PageSize=${pageSize}&Filter=${filter}`
        );

        if (isFailurePayload(res)) break;

        const items = norm(res);
        if (!items.length) break;

        for (const it of items) {
          const laneId = it?.laneId ?? it?.LaneId ?? it?.LaneID ?? null;
          if (laneId == null) continue;

          const key = String(laneId);
          if (seen.has(key)) continue;
          seen.add(key);

          const originRaw =
            it?.sourceLocation ??
            it?.SourceLocation ??
            it?.origin ??
            it?.Origin ??
            it?.laneSource ??
            it?.LaneSource ??
            it?.LaneOrigin ??
            it?.laneOrigin ??
            "";

          const destinationRaw =
            it?.destinationLocation ??
            it?.DestinationLocation ??
            it?.destination ??
            it?.Destination ??
            it?.laneDestination ??
            it?.LaneDestination ??
            it?.LaneDestinationName ??
            it?.laneDestinationName ??
            "";

          const origin = String(originRaw || "").trim();
          const destination = String(destinationRaw || "").trim();

          acc.push({
            ...it,
            LaneId: Number(laneId),
            Origin: origin,
            Destination: destination,
            LaneSource: origin,
            LaneDestination: destination,
            IsActive:
              it?.inactive != null
                ? !it.inactive
                : it?.Inactive != null
                  ? !it.Inactive
                  : true,
          });
        }

        const totalPages = Number(
          res?.TotalPages ??
          res?.totalPages ??
          (items.length < pageSize ? page : page + 1)
        );

        if (page >= totalPages) break;
        page += 1;
      }

      setAllLanes(acc);
      return acc;
    },
    [tenderLanesSummaryService]
  );

  useEffect(() => {
    let cancelled = false;
    const lineId = sessionLine?.Id;
    if (!lineId) return;

    (async () => {
      try {
        if (isSpotTender) {
          const res = await loadLanesPaged({
            lineId,
            isSpot: true,
            phaseId: null,
          });

          if (!cancelled && Array.isArray(res)) setLanes(res);
          return;
        }

        if (lanesPhaseId == null) return;

        const res = await loadLanesPaged({
          lineId,
          isSpot: false,
          phaseId: lanesPhaseId,
        });

        if (!cancelled && Array.isArray(res)) setLanes(res);
      } catch {
        if (!cancelled) setLanes([]);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [sessionLine?.Id, isSpotTender, lanesPhaseId, loadLanesPaged]);

  useEffect(() => {
    let cancelled = false;
    const lineId = sessionLine?.Id;
    if (!lineId) return;

    (async () => {
      try {
        const res = await loadAllLanesPaged({ lineId });
        if (!cancelled && Array.isArray(res)) setAllLanes(res);
      } catch {
        if (!cancelled) setAllLanes([]);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [sessionLine?.Id, loadAllLanesPaged]);

  // ===============================================================================================
  // Summary rows
  // ===============================================================================================

  /**
   * Builds the rows consumed by TenderSummaryTable.
   *
   * Numeric metrics come from the current accepted lane summaries for the whole Tender.
   * Snapshot metadata comes from TenderLanes.
   *
   * Satisfaction precedence:
   * - Official snapshot values from TenderLanes are used when present.
   * - When the snapshot still says "not satisfied", but accepted loads already
   *   reach Tender.Loads, a virtual current satisfied state is projected:
   *      * IsSatisfied = true
   *      * SatisfiedAt = "Current"
   *      * SatisfiedInPhase = "Current" for Non-Spot
   *
   * @returns {Array} Summary rows.
   */
  const summaryLaneRows = useMemo(() => {
    const lanes = Array.isArray(hvAllLanes) ? hvAllLanes : [];
    const snapshotRows = Array.isArray(hvSummaryRows) ? hvSummaryRows : [];
    const acceptedRows = Array.isArray(hvLaneSummariesAccepted)
      ? hvLaneSummariesAccepted
      : [];

    const snapshotByLane = new Map();
    const acceptedByLane = new Map();
    const phaseById = new Map((hvPhases || []).map((p) => [p.Id, p]));
    const tenderLoads = Number(hvTender?.Loads ?? 0);

    for (const r of snapshotRows) {
      const laneIdRaw = r?.LaneId ?? r?.laneId ?? r?.Id ?? r?.id ?? null;
      const laneId = laneIdRaw != null ? Number(laneIdRaw) : null;
      if (!Number.isFinite(laneId)) continue;
      snapshotByLane.set(laneId, r);
    }

    for (const r of acceptedRows) {
      const laneIdRaw = r?.LaneId ?? r?.laneId ?? r?.Id ?? r?.id ?? null;
      const laneId = laneIdRaw != null ? Number(laneIdRaw) : null;
      if (!Number.isFinite(laneId)) continue;
      acceptedByLane.set(laneId, r);
    }

    const asNum = (v) => {
      const n = Number(v);
      return Number.isFinite(n) ? n : null;
    };

    return lanes
      .map((lane) => {
        const laneId = Number(getLaneId(lane));
        if (!Number.isFinite(laneId)) return null;

        const snapshot = snapshotByLane.get(laneId) || null;
        const accepted = acceptedByLane.get(laneId) || null;

        const origin =
          (getLaneOrigin(lane) || "").trim() ||
          String(
            snapshot?.SourceLocation ??
            snapshot?.sourceLocation ??
            snapshot?.LaneSource ??
            snapshot?.laneSource ??
            snapshot?.Origin ??
            snapshot?.origin ??
            ""
          ).trim() ||
          "-";

        const destination =
          (getLaneDestination(lane) || "").trim() ||
          String(
            snapshot?.DestinationLocation ??
            snapshot?.destinationLocation ??
            snapshot?.LaneDestination ??
            snapshot?.laneDestination ??
            snapshot?.Destination ??
            snapshot?.destination ??
            ""
          ).trim() ||
          "-";

        // -------------------------------------------------------------------------------------------
        // Numeric values always come from accepted quotes for the whole Tender.
        // When a lane has no accepted quotes yet, the numeric values fall back to zero/null presentation.
        // -------------------------------------------------------------------------------------------
        const totalQuotes = asNum(accepted?.TotalQuotes ?? accepted?.totalQuotes) ?? 0;
        const totalBidValue = asNum(
          accepted?.TotalBidValue ?? accepted?.totalBidValue
        );
        const maxBidValue = asNum(accepted?.MaxBidValue ?? accepted?.maxBidValue);
        const minBidValue = asNum(accepted?.MinBidValue ?? accepted?.minBidValue);
        const totalLoads = asNum(accepted?.TotalLoads ?? accepted?.totalLoads) ?? 0;
        const maxLoads = asNum(accepted?.MaxLoads ?? accepted?.maxLoads);
        const minLoads = asNum(accepted?.MinLoads ?? accepted?.minLoads);
        const maxEstimatedTransitDays = asNum(
          accepted?.MaxEstimatedTransitDays ?? accepted?.maxEstimatedTransitDays
        );
        const minEstimatedTransitDays = asNum(
          accepted?.MinEstimatedTransitDays ?? accepted?.minEstimatedTransitDays
        );

        // -------------------------------------------------------------------------------------------
        // Snapshot metadata remains the authoritative source for persisted satisfied information.
        // -------------------------------------------------------------------------------------------
        const snapshotIsSatisfied = Boolean(
          snapshot?.IsSatisfied ?? snapshot?.isSatisfied ?? false
        );
        const snapshotSatisfiedAt = getSummarySatisfiedAt(snapshot);
        const snapshotSatisfiedPhaseId = getSummarySatisfiedPhaseId(snapshot);

        const snapshotPhase =
          snapshotSatisfiedPhaseId != null
            ? phaseById.get(snapshotSatisfiedPhaseId) || null
            : null;

        const snapshotSatisfiedPhaseName =
          snapshot?.SatisfiedInTenderPhase ??
          snapshot?.satisfiedInTenderPhase ??
          snapshotPhase?.Name ??
          snapshotPhase?.name ??
          null;

        // -------------------------------------------------------------------------------------------
        // Virtual satisfied state:
        // If the snapshot is still unsatisfied, but current accepted loads already reach
        // the Tender total loads, the table should present the lane as satisfied "Current".
        // -------------------------------------------------------------------------------------------
        const virtualSatisfied =
          !snapshotIsSatisfied &&
          !snapshotSatisfiedAt &&
          tenderLoads > 0 &&
          totalLoads >= tenderLoads;

        const isSatisfied = snapshotIsSatisfied || virtualSatisfied;

        const satisfiedAt = snapshotSatisfiedAt
          ? snapshotSatisfiedAt
          : virtualSatisfied
            ? "Current"
            : null;

        const satisfiedInPhase = snapshotSatisfiedPhaseName
          ? snapshotSatisfiedPhaseName
          : virtualSatisfied && !isSpotTender
            ? (t("CurrentPhase") || "Current")
            : null;

        return {
          laneId,
          origin,
          destination,
          totalQuotes,
          totalBidValue,
          maxBidValue,
          minBidValue,
          totalLoads,
          maxLoads,
          minLoads,
          maxEstimatedTransitDays,
          minEstimatedTransitDays,
          isSatisfied,
          satisfiedAt,
          satisfiedInPhase,
        };
      })
      .filter(Boolean);
  }, [hvAllLanes, hvSummaryRows, hvLaneSummariesAccepted, hvPhases, hvTender?.Loads, isSpotTender, t]);

  // ===============================================================================================
  // UI helpers
  // ===============================================================================================

  /**
   * Opens the carrier detail view and refreshes its data.
   *
   * @param {number|null} carrierId - Selected carrier id.
   * @param {number|null} phaseId - Selected phase id.
   * @returns {void}
   */
  const refreshCarrierDetailTable = useCallback((carrierId, phaseId) => {
    setSelectedCarrierId(carrierId);
    setActiveView("carrier");

    if (phaseId != null) setDetailPhaseId(phaseId);

    setRefreshKey((prev) => prev + 1);
  }, []);

  /**
   * Handles a phase switch inside the carrier detail view.
   *
   * The carrier selection is reset because the available carrier universe can
   * change when the phase changes.
   *
   * @param {number|null} newPhaseId - Newly selected phase id.
   * @returns {void}
   */
  const handleDetailPhaseChange = useCallback((newPhaseId) => {
    setDistinctCarriersItems([]);
    setSelectedCarrierId(null);
    setDetailPhaseId(newPhaseId ?? null);
    setDistinctCarriersReloadKey((prev) => prev + 1);
  }, []);

  /**
   * Accepts or unaccepts a quote and updates local quote collections immediately.
   *
   * The local fast path keeps the UI responsive while aggregate endpoints are
   * reloaded in the background by refreshKey-driven effects.
   *
   * @param {object} args - Mutation arguments.
   * @param {number} args.quoteId - Quote id.
   * @param {boolean} args.nextAccepted - Next accepted state.
   * @returns {Promise<void>}
   */
  const setQuoteAccepted = useCallback(
    async ({ quoteId, nextAccepted }) => {
      if (!quoteId) return;
      if (acceptBusyIdsRef.current.has(quoteId)) return;

      incLoad();

      setAcceptBusyIds((prev) => {
        const next = new Set(prev);
        next.add(quoteId);
        return next;
      });

      try {
        const action = nextAccepted ? "accept" : "unaccept";

        const res = await tenderQuotesUpdateService.postQuery(
          `/${quoteId}/${action}`
        );

        if (isFailurePayload(res)) {
          toastRef.current?.("error", "", apiMessage(res, t("Erro")));
          return;
        }

        setQuotes((prev) => {
          const list = Array.isArray(prev) ? prev : [];
          const next = list.map((q) => {
            const id = getQuoteId(q);
            if (id !== quoteId) return q;

            return {
              ...q,
              IsAccepted: nextAccepted,
              isAccepted: nextAccepted,
            };
          });

          setQuotesAccepted(next.filter((q) => getQuoteAccepted(q)));
          setQuotesUnaccepted(next.filter((q) => !getQuoteAccepted(q)));

          return next;
        });

        setLastSyncAt(Date.now());
        setRefreshKey((prev) => prev + 1);
      } catch (err) {
        toastRef.current?.("error", "", apiMessage(err, t("Erro")));
      } finally {
        setDistinctCarriersReloadKey((prev) => prev + 1);
        decLoad();

        setAcceptBusyIds((prev) => {
          const next = new Set(prev);
          next.delete(quoteId);
          return next;
        });
      }
    },
    [decLoad, incLoad, t, tenderQuotesUpdateService]
  );

  const [showCreatePhaseConfirm, setShowCreatePhaseConfirm] = useState(false);
  const [showFinishConfirm, setShowFinishConfirm] = useState(false);

  /**
   * Opens the finish tender confirmation dialog.
   *
   * @returns {void}
   */
  const handleFinish = useCallback(() => {
    if (!sessionLine) return;
    setShowFinishConfirm(true);
  }, [sessionLine]);

  /**
   * Confirms the tender finish action.
   *
   * @returns {void}
   */
  const confirmFinishTender = useCallback(() => {
    setShowFinishConfirm(false);

    if (!sessionLine?.Id) return;

    incLoad();

    tenderServiceUpdate
      .update(sessionLine.Id, { isFinished: true })
      .then((res) => {
        if (isFailurePayload(res)) {
          toastRef.current?.("error", "", apiMessage(res, t("ErrorSaving")));
          return;
        }

        toastRef.current?.("success", "", t("TenderFinished"));
        handleTabClose(id);
      })
      .catch((err) =>
        toastRef.current?.("error", "", apiMessage(err, t("ErrorSaving")))
      )
      .finally(() => {
        decLoad();
      });
  }, [
    incLoad,
    decLoad,
    tenderServiceUpdate,
    sessionLine?.Id,
    t,
    handleTabClose,
    id,
  ]);

  /**
   * Navigates to the create-phase flow using the currently loaded context.
   *
   * @returns {void}
   */
  const confirmCreatePhase = useCallback(() => {
    if (!sessionLine?.Id) return;

    setShowCreatePhaseConfirm(false);
    incLoad();

    try {
      let lastPhase = null;

      if (phases.length === 1) {
        lastPhase = phases[0];
      } else if (phases.length > 1) {
        const lastIds = phases
          .map((p) => p.LastTenderPhaseId)
          .filter((pid) => pid !== null);

        lastPhase = phases.find((p) => !lastIds.includes(p.Id)) || null;
      }

      navigate("/home/create-tenders", {
        state: {
          isCreatePhase: true,
          tenderInfo,
          lastPhase,
          phases,
          quotes: hvQuotesAll,
          lanes,
          carriers,
          distinctCarriersItems,
        },
      });
    } catch {
      toastRef.current?.("error", "", t("ErrorFinishingLastPhase"));
    } finally {
      decLoad();
    }
  }, [
    sessionLine?.Id,
    incLoad,
    phases,
    navigate,
    tenderInfo,
    hvQuotesAll,
    lanes,
    carriers,
    distinctCarriersItems,
    t,
    decLoad,
  ]);

  const carriersWithQuotes = useMemo(() => {
    if (!Array.isArray(distinctCarriersItems) || !distinctCarriersItems.length)
      return [];

    return distinctCarriersItems
      .map((row) => {
        const carrier = row?.Carrier ?? row?.carrier ?? row ?? {};
        const carrierId = carrier?.CarrierId ?? carrier?.Id ?? null;

        return {
          CarrierId: carrierId,
          CarrierName: carrier?.CarrierName ?? carrier?.Name ?? "",
          Name: carrier?.CarrierName ?? carrier?.Name ?? "",
          ...carrier,
          QuotesCount: row?.QuotesCount ?? row?.quotesCount ?? null,
          QuotesAcceptedCount:
            row?.QuotesAcceptedCount ?? row?.quotesAcceptedCount ?? null,
        };
      })
      .filter((c) => c.CarrierId != null);
  }, [distinctCarriersItems]);

  useEffect(() => {
    if (activeView !== "carrier") return;
    if (selectedCarrierId != null) return;
    if (!Array.isArray(carriersWithQuotes) || carriersWithQuotes.length === 0)
      return;

    const firstId =
      carriersWithQuotes[0]?.CarrierId ?? carriersWithQuotes[0]?.Id;

    if (firstId != null) setSelectedCarrierId(firstId);
  }, [activeView, selectedCarrierId, carriersWithQuotes]);

  // ===============================================================================================
  // Phase-indexed quotes for carrier view
  // ===============================================================================================

  const quotesByPhaseId = useMemo(() => {
    const map = new Map();
    if (isSpotTender) return map;

    for (const q of hvQuotesAll || []) {
      const pid = getQuotePhaseId(q);
      if (pid == null) continue;

      if (!map.has(pid)) map.set(pid, []);
      map.get(pid).push(q);
    }

    return map;
  }, [hvQuotesAll, isSpotTender]);

  const detailQuotes = useMemo(() => {
    if (isSpotTender) return hvQuotesAll;
    if (detailPhaseId == null) return [];
    return quotesByPhaseId.get(detailPhaseId) || [];
  }, [hvQuotesAll, isSpotTender, detailPhaseId, quotesByPhaseId]);

  // ===============================================================================================
  // Render
  // ===============================================================================================

  return (
    <>
      <MHeader
        element={
          <>
            <MButton
              tooltip={t("InQuotation")}
              icon={
                <i
                  className="pi pi-chart-line iconTable"
                  style={{ color: "var(--color-green-light)" }}
                />
              }
              onClick={() => setActiveView("inQuotation")}
              label={t("InQuotation")}
            />

            <MButton
              tooltip={t("Carrier")}
              icon={
                <i
                  className="pi pi-truck"
                  style={{ color: "var(--color-green-light)" }}
                />
              }
              onClick={() => setActiveView("carrier")}
              label={t("ViewCarriers")}
            />

            <MButton
              tooltip={t("Lane")}
              icon={
                <i
                  className="pi pi-arrow-right-arrow-left iconTable"
                  style={{ color: "var(--color-green-light)" }}
                />
              }
              onClick={() => setActiveView("lane")}
              label={t("ViewLanes")}
            />

            <MButton
              tooltip={t("Summary") || "Summary"}
              icon={
                <i
                  className="pi pi-chart-bar iconTable"
                  style={{ color: "var(--color-green-light)" }}
                />
              }
              onClick={() => setActiveView("summary")}
              label={t("ViewSummary") || "Summary"}
            />

            {!isSpotTender && (
              <MButton
                tooltip={t("CreateNewTenderPhase")}
                icon={
                  <i
                    className="pi pi-plus-circle iconTable"
                    style={{ color: "var(--color-red)" }}
                  />
                }
                onClick={() => setShowCreatePhaseConfirm(true)}
                label={t("CreateNewTenderPhase")}
              />
            )}

            <MButton
              tooltip={t("FinishTender")}
              icon={
                <i
                  className="pi pi-check iconTable"
                  style={{ color: "var(--color-red)" }}
                />
              }
              onClick={handleFinish}
              label={t("FinishTender")}
            />
          </>
        }
      />

      <MMessageBoard
        title={t("ConfirmFinishTender")}
        open={showFinishConfirm}
        onClose={() => setShowFinishConfirm(false)}
        warning
        body={
          <div className="tiq-message-body">
            {t("AreYouSureYouWantToFinishTender")}
          </div>
        }
        footer={
          <>
            <MButton
              label={t("Cancel")}
              backgroundColor={"var(--color-red)"}
              labelColor={"var(--color-white)"}
              icon={
                <i
                  className="pi pi-times iconTable"
                  style={{ color: "white" }}
                />
              }
              onClick={() => setShowFinishConfirm(false)}
            />
            <MButton
              label={t("Confirm")}
              onClick={confirmFinishTender}
              backgroundColor={"var(--color-green)"}
              labelColor={"var(--color-white)"}
              icon={
                <i
                  className="pi pi-check iconTable"
                  style={{ color: "white" }}
                />
              }
            />
          </>
        }
      />

      <MMessageBoard
        title={t("ConfirmCreateNewPhase")}
        open={showCreatePhaseConfirm}
        onClose={() => setShowCreatePhaseConfirm(false)}
        warning
        body={
          <div className="tiq-message-body">
            {t("AreYouSureYouWantToCreateNewPhase")}
          </div>
        }
        footer={
          <>
            <MButton
              label={t("Cancel")}
              backgroundColor={"var(--color-red)"}
              labelColor={"var(--color-white)"}
              icon={
                <i
                  className="pi pi-times iconTable"
                  style={{ color: "white" }}
                />
              }
              onClick={() => setShowCreatePhaseConfirm(false)}
            />
            <MButton
              label={t("Confirm")}
              onClick={confirmCreatePhase}
              backgroundColor={"var(--color-green)"}
              labelColor={"var(--color-white)"}
              icon={
                <i
                  className="pi pi-check iconTable"
                  style={{ color: "white" }}
                />
              }
            />
          </>
        }
      />

      <div className="tiq-page-root">
        {activeView === "inQuotation" && (
          <div className="firstDivContainer tiq-main-view">
            <div className="tiq-form-card">
              <InQuotationForm
                tenderInfo={hvTender}
                phases={hvPhases}
                setTenderInfo={() => { }}
                isSpotTender={isSpotTender}
                selectedPhaseId={selectedPhaseId}
                setSelectedPhaseId={setSelectedPhaseId}
                tenderSchemaOptions={tenderSchemaOptions}
              />
            </div>

            <div className="tiq-panels-row">
              <div className="tiq-panel-card">
                <CarrierResponsesTable
                  tenderId={hvTender?.Id}
                  phases={hvPhases}
                  responses={hvQuotesAll}
                  carriers={carriersWithQuotes}
                  isSpotTender={isSpotTender}
                  setActiveView={setActiveView}
                  setSelectedCarrierId={setSelectedCarrierId}
                />
              </div>

              <div className="tiq-panel-card">
                <TenderQuotesAnalysis
                  tenderId={hvTender?.Id}
                  routeId={hvTender?.RouteId}
                  isSpotTender={isSpotTender}
                  lanes={hvLanes}
                  laneSummaries={hvLaneSummaries}
                />
              </div>
            </div>
          </div>
        )}

        {activeView === "carrier" && (
          <div className="tiq-carrier-detail-card">
            <span className="tiq-carrier-detail-title">
              {t("Carrier Responses")}
            </span>

            <div className="tiq-scroll-x">
              <div className="tiq-scroll-inner">
                <div className="tiq-carrier-detail-filters">
                  {!isSpotTender && (
                    <MSelect
                      label={t("Phase")}
                      value={
                        hvPhases
                          .map((phase) => ({
                            label: phase.Name || `${t("Phase")} ${phase.Id}`,
                            value: phase.Id,
                          }))
                          .find((opt) => opt.value === detailPhaseId) || null
                      }
                      onChangeValue={(newValue) => {
                        if (newValue && typeof newValue === "object") {
                          handleDetailPhaseChange(newValue.value);
                        } else {
                          handleDetailPhaseChange(null);
                        }
                      }}
                      options={hvPhases.map((phase) => ({
                        label: phase.Name || `${t("Phase")} ${phase.Id}`,
                        value: phase.Id,
                      }))}
                      autocomplete
                      required
                    />
                  )}

                  <MSelect
                    label={t("Carrier")}
                    value={
                      carriersWithQuotes
                        .map((c) => ({
                          label: c.CarrierName || c.Name,
                          value: c.CarrierId || c.Id,
                        }))
                        .find(
                          (opt) => opt.value === (selectedCarrierId ?? null)
                        ) || null
                    }
                    onChangeValue={(newValue) => {
                      if (newValue && typeof newValue === "object") {
                        setSelectedCarrierId(newValue.value);
                      } else {
                        setSelectedCarrierId(null);
                      }
                    }}
                    options={carriersWithQuotes.map((c) => ({
                      label: c.CarrierName || c.Name,
                      value: c.CarrierId || c.Id,
                    }))}
                    autocomplete
                    required
                  />

                  <MSelect
                    label={t("BestMetric")}
                    value={{
                      label:
                        bestMetric === "bid"
                          ? t("BidValue")
                          : bestMetric === "loads"
                            ? t("Loads")
                            : t("EstimatedTransitDays"),
                      value: bestMetric,
                    }}
                    onChangeValue={(newValue) => {
                      if (newValue && typeof newValue === "object") {
                        setBestMetric(newValue.value);
                      } else {
                        setBestMetric("bid");
                      }
                    }}
                    options={[
                      { label: t("BidValue"), value: "bid" },
                      { label: t("Loads"), value: "loads" },
                      { label: t("EstimatedTransitDays"), value: "etd" },
                    ]}
                    autocomplete
                    required
                  />
                </div>
              </div>
            </div>

            <div className="tiq-scroll-x tiq-carrier-detail-table-wrapper">
              <div className="tiq-scroll-inner">
                <CarrierResponseDetailTable
                  tenderInfo={hvTender}
                  selectedCarrier={selectedCarrierId}
                  selectedPhaseId={isSpotTender ? null : detailPhaseId}
                  quotes={detailQuotes}
                  lanes={hvLanes}
                  isSpotTender={isSpotTender}
                  bestMetric={bestMetric}
                  laneBestMap={laneBestMap}
                  acceptBusyIds={acceptBusyIds}
                  onSetQuoteAccepted={setQuoteAccepted}
                  onRefresh={() =>
                    refreshCarrierDetailTable(
                      selectedCarrierId,
                      isSpotTender ? null : detailPhaseId
                    )
                  }
                />
              </div>
            </div>
          </div>
        )}

        {activeView === "lane" && (
          <div className="tiq-carrier-detail-card">
            <span className="tiq-carrier-detail-title">{t("Lane")}</span>

            <div className="tiq-scroll-x">
              <div className="tiq-scroll-inner">
                <div className="tiq-carrier-detail-filters">
                  <MSelect
                    label={t("BestMetric")}
                    value={{
                      label:
                        bestMetric === "bid"
                          ? t("BidValue")
                          : bestMetric === "loads"
                            ? t("Loads")
                            : t("EstimatedTransitDays"),
                      value: bestMetric,
                    }}
                    onChangeValue={(newValue) => {
                      if (newValue && typeof newValue === "object") {
                        setBestMetric(newValue.value);
                      } else {
                        setBestMetric("bid");
                      }
                    }}
                    options={[
                      { label: t("BidValue"), value: "bid" },
                      { label: t("Loads"), value: "loads" },
                      { label: t("EstimatedTransitDays"), value: "etd" },
                    ]}
                    autocomplete
                    required
                  />
                </div>
              </div>
            </div>

            <div className="tiq-scroll-x tiq-carrier-detail-table-wrapper">
              <div className="tiq-scroll-inner">
                <ViewLaneTable
                  tenderInfo={hvTender}
                  acceptedQuotes={hvQuotesAcceptedEnriched}
                  allQuotes={hvQuotesAllEnriched}
                  lanes={hvAllLanes}
                  carriers={carriersWithQuotes}
                  onRefresh={() => setRefreshKey((prev) => prev + 1)}
                  metricKey={bestMetric === "etd" ? "transit" : bestMetric}
                  isFinished={isTenderFinished}
                  isSpotTender={isSpotTender}
                  currentPhaseId={lastPhaseId}
                  laneBestMap={laneBestMap}
                  acceptBusyIds={acceptBusyIds}
                  onSetQuoteAccepted={setQuoteAccepted}
                />
              </div>
            </div>
          </div>
        )}

        {activeView === "summary" && (
          <div className="tiq-carrier-detail-card">
            <span className="tiq-carrier-detail-title">
              {t("Summary") || "Summary"}
            </span>

            <div className="tiq-scroll-x tiq-carrier-detail-table-wrapper">
              <div className="tiq-scroll-inner">
                <TenderSummaryTable
                  tenderInfo={hvTender}
                  isSpotTender={isSpotTender}
                  rows={summaryLaneRows}
                  onRefresh={() => setRefreshKey((prev) => prev + 1)}
                />
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}

TenderInQuotationPage.propTypes = {
  handleTabClose: PropTypes.func,
  toastMessages: PropTypes.func,
};